import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useState,
} from 'react';

import ConfirmModal from '@/components/shared/ConfirmModal';
import { ConfirmModalVariant } from '@/components/shared/ConfirmModal/types';

type ConfirmOptions = {
  onConfirm: () => void | Promise<unknown>;
  onError?: (error: unknown) => void;
  title?: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmModalVariant;
};

type Confirm = (options: ConfirmOptions) => void;

const ConfirmModalContext = createContext<Confirm | null>(null);

export function ConfirmModalProvider({ children }: PropsWithChildren) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const close = () => {
    setOptions(null);
    setIsLoading(false);
  };

  const handleConfirm = async () => {
    if (!options) return;
    try {
      setIsLoading(true);
      await options.onConfirm();
    } catch (error) {
      options.onError?.(error);
    }
    close();
  };

  return (
    <ConfirmModalContext.Provider value={setOptions}>
      {children}
      {options !== null && (
        <ConfirmModal
          visible
          onClose={close}
          onConfirm={handleConfirm}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          isLoading={isLoading}
        />
      )}
    </ConfirmModalContext.Provider>
  );
}

export const useConfirm = (): Confirm => {
  const ctx = useContext(ConfirmModalContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmModalProvider');
  }
  return ctx;
};

export default ConfirmModalContext;
